import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';

import { appConfig } from 'brand-config';
import useSession from 'core/hooks/use-session';
import Checkbox from 'core/components/checkbox';
import { Label } from 'core/components/label';
import {
  COSMETIC_DAMAGE_UW_OVERRIDE_BLOCKED_NOTE,
  cosmeticDamageUwOverrideFieldValues,
  isCosmeticDamageUwOverridden,
  isCosmeticDamageUwOverrideBlockedByAcv
} from 'common/helpers/cosmetic-damage-exclusion-helpers';

import useStyles from './exclusions.styles';

/**
 * Whether the current user may override a risk's Cosmetic Damage Exclusion (#35739): an underwriter
 * (CanApproveUWReview — the same authority the FO-6 sub-floor approval uses) on a brand that locks
 * the exclusion and opts into the override (brand-config featureFlags.cosmeticDamageExclusionUwOverride,
 * CFM).
 */
export const useCanOverrideCosmeticDamageExclusion = () => {
  const session = useSession();
  return Boolean(session?.canApproveUWReview) && Boolean(appConfig?.featureFlags?.cosmeticDamageExclusionUwOverride);
};

/** Writes the marker + the four exclusion flags together, marking them touched without dirtying the form. */
const writeOverride = ({ fieldName, overridden, setFieldValue, setFieldTouched }) => {
  for (const [field, value] of Object.entries(cosmeticDamageUwOverrideFieldValues(overridden))) {
    setFieldValue(`${fieldName}.${field}`, value);
    setFieldTouched(`${fieldName}.${field}`, true, false);
  }
};

/**
 * #35870: switching an overridden risk INTO an ACV settlement (building, structure or ACV roof) withdraws
 * the override — the exclusion comes back on and the marker is cleared, since the override only applies
 * to RC risks. Only a change into ACV does this: a risk that loads as ACV + overridden (a migrated
 * legacy "Included" ACV building) is left exactly as stored.
 */
export const useClearCosmeticDamageUwOverrideWhenAcv = ({ fieldName, coverages }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const blockedByAcv = isCosmeticDamageUwOverrideBlockedByAcv(coverages);
  const overridden = isCosmeticDamageUwOverridden(coverages);
  const wasBlockedByAcvRef = useRef(blockedByAcv);

  useEffect(() => {
    const switchedToAcv = blockedByAcv && !wasBlockedByAcvRef.current;
    wasBlockedByAcvRef.current = blockedByAcv;
    if (switchedToAcv && overridden) {
      writeOverride({ fieldName, overridden: false, setFieldValue, setFieldTouched });
    }
  }, [blockedByAcv, overridden, fieldName, setFieldValue, setFieldTouched]);
};

/**
 * "UW Override" checkbox shown under the locked Cosmetic Damage Exclusion toggle (#35739, #35870). The
 * checkbox IS the override: checked ⇒ the exclusion and all three surfacing-type flags come off and
 * `cosmeticDamageUwOverride` is persisted on the risk (the dec/app/quote print "Cosmetic Damage:
 * Included", FO 5562 attaches only while another risk still carries the exclusion); unchecked ⇒ they all
 * come back on and the marker is cleared so the brand rules apply again. Offered only on RC
 * building/dwelling risks without an ACV roof (an ACV building, structure or roof settlement replaces
 * the checkbox with a note saying why; RC with ACV for wind/hail is fine) to underwriters, on brands
 * with the flag, and never on a disabled (view-only) form.
 *
 * `fieldName` is the risk's coverages path (e.g. `farmLocations[0].risks[0].coverages`).
 */
const CosmeticDamageUwOverride = ({ fieldName, coverages, disabled }) => {
  const classes = useStyles();
  const canOverride = useCanOverrideCosmeticDamageExclusion();
  const { setFieldValue, setFieldTouched } = useFormikContext();

  if (!canOverride || disabled) {
    return null;
  }

  // Blocked by an ACV settlement: say so instead of silently omitting the control (devlo, #35870) — on
  // FO-6 the roof is forced by the structure settlement, so the underwriter may not see why otherwise.
  if (isCosmeticDamageUwOverrideBlockedByAcv(coverages)) {
    return (
      <Grid item xs={12}>
        <Label type="infoLabel" className={classes.uwOverride}>
          {COSMETIC_DAMAGE_UW_OVERRIDE_BLOCKED_NOTE}
        </Label>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Checkbox
        id={`${fieldName}.cosmeticDamageUwOverride`}
        mode="dark"
        className={classes.uwOverride}
        label="UW Override – remove the Cosmetic Damage Exclusion from this risk (Cosmetic Damage Included)"
        value={isCosmeticDamageUwOverridden(coverages)}
        onChange={(event) =>
          writeOverride({
            fieldName,
            overridden: Boolean(event?.target?.checked),
            setFieldValue,
            setFieldTouched
          })
        }
      />
    </Grid>
  );
};

CosmeticDamageUwOverride.propTypes = {
  /** The risk's coverages path in the form, e.g. `farmLocations[0].risks[0].coverages`. */
  fieldName: PropTypes.string.isRequired,
  coverages: PropTypes.object,
  /** The enclosing risk form is not editable (view-only, locked term): no override offered. */
  disabled: PropTypes.bool
};

CosmeticDamageUwOverride.defaultProps = {
  coverages: null,
  disabled: false
};

export default CosmeticDamageUwOverride;
